import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { from, Observable, of } from 'rxjs';
import { map, skipWhile, withLatestFrom } from 'rxjs/operators';

import { AutocompleteRegion, RecentlyBookedHotel, TopRegion } from '@rx-applications/clientsite/shared/config/utils';

import { EnvironmentFlag } from '../constants';
import { ConfigState } from '../state/config.models';
import * as fromConfigSelectors from '../state/config.selectors';
import {
  baseConfigActions,
  cidActions,
  clientConfig,
  localeActions,
  setSelectedCurrency,
  updateRecentlyBookedHotel,
} from '../state/config.actions';
import { FeatureFlagEnum } from '@rx-applications/shared/clientsite/feature-flags';

@Injectable({
  providedIn: 'root',
})
export class ConfigFacade {
  locale$ = this.store.select(fromConfigSelectors.selectLocale);
  baseConfiguration$ = this.store.select(fromConfigSelectors.selectBaseConfiguration);
  cid$ = this.store.select(fromConfigSelectors.selectCid);
  environmentConfiguration$ = this.store.select(fromConfigSelectors.selectEnvironmentConfiguration);
  error$ = this.store.select(fromConfigSelectors.selectError);
  websiteId$ = this.store.select(fromConfigSelectors.selectWebsiteId);
  companyName$ = this.store.select(fromConfigSelectors.selectCompanyName);
  companyNameWithApostrophe$ = this.store.select(fromConfigSelectors.selectCompanyNameWithApostrophe);
  defaultLocale$ = this.store.select(fromConfigSelectors.selectDefaultLocale);
  isApprovalFlowEnabled$ = this.store.select(fromConfigSelectors.selectApprovalFlowEnabled);

  isAuthenticatedAndConfigured$ = this.store.select(fromConfigSelectors.selectIsAuthenticatedAndConfigured);
  hasUserConfigurationHttpCompleted$ = this.store.select(fromConfigSelectors.selectHasUserConfigurationHttpCompleted);

  contactNumber$ = this.store.select(fromConfigSelectors.selectContactNumber);

  paymentCallbackUrl$ = this.store.select(fromConfigSelectors.selectPaymentCallbackUrl);

  hasEnvironmentConfigurationHttpCompleted$ = this.store.select(
    fromConfigSelectors.selectHasEnvironmentConfigurationHttpCompleted,
  );
  hasBaseConfigurationHttpCompleted$ = this.store.select(fromConfigSelectors.selectHasBaseConfigurationHttpCompleted);

  isShowCPLGroupBookingEnabled$ = this.isFeatureEnabled(FeatureFlagEnum.ShowCPLGroupBooking);

  isShowCPLMeetingVenueEnabled$ = this.isFeatureEnabled(FeatureFlagEnum.ShowCPLMeetingVenue);

  useBookingsModuleFromMonorepoLibrary$ = this.isFeatureEnabled(FeatureFlagEnum.UseBookingsModuleFromMonorepoLibrary);

  useClientsiteAuthApp$ = of(true);

  saverplusSameDayRate$ = this.isFeatureEnabled(FeatureFlagEnum.SaverplusSameDayRate);

  loyaltyViaSpecialRequest$ = this.isFeatureEnabled(FeatureFlagEnum.LoyaltyViaSpecialRequest);

  isRoomexPaySalesforceIntegrationEnabled$ = this.store.select(
    fromConfigSelectors.selectEnvironmentFeatures(EnvironmentFlag.RoomexPaySalesforceIntegration),
  );

  isShowEvParkingFilterEnabled$: Observable<boolean> = this.store.select(
    fromConfigSelectors.selectEnvironmentFeatures(EnvironmentFlag.ShowEVParkingFilter),
  );

  isCidEnabled$ = this.isFeatureEnabled(FeatureFlagEnum.DisplayCid).pipe(
    withLatestFrom(from([window.navigator.webdriver])),
    map(([isFeatureEnabled, isUsingWebdriver]) => isFeatureEnabled || isUsingWebdriver),
  );

  isIntercomDisabled$ = this.isFeatureEnabled(FeatureFlagEnum.DisableIntercom);

  isPwaEnabled$ = this.store.select(fromConfigSelectors.selectEnvironmentFeatures(EnvironmentFlag.Pwa));

  isGetReservationDetailsSoldoutEnabled$ = this.store.select(
    fromConfigSelectors.selectEnvironmentFeatures(EnvironmentFlag.GetReservationDetailsSoldout),
  );

  isMultiroomFilteringEnabled$ = this.store.select(
    fromConfigSelectors.selectEnvironmentFeatures(EnvironmentFlag.MultiroomFilter),
  );

  isTrainlineTicketsBtnVisable$ = this.isFeatureEnabled(FeatureFlagEnum.ShowTrainlineTicketsButton);

  stripePublishableKey$ = this.store.select(fromConfigSelectors.selectStripePublishableKey);
  trainlineHyperlink$ = this.store.select(fromConfigSelectors.selectTrainlineHyperlink);

  userConfiguration$ = this.store.select(fromConfigSelectors.selectClientConfiguration);
  userGeneralConfiguration$ = this.store.select(fromConfigSelectors.selectUserGeneralConfiguration);
  loyaltyEnabled$ = this.store.select(fromConfigSelectors.selectLoyaltyEnabled);
  userEmailAddress$ = this.store.select(fromConfigSelectors.selectUserEmailAddress);
  userFullName$ = this.store.select(fromConfigSelectors.selectUserFullName);
  hasInPolicyHidden$ = this.store.select(fromConfigSelectors.selectHideInPolicy);

  hasPolicyEnabled$ = this.store.select(fromConfigSelectors.selectHasPolicyEnabled);
  selectAreEmergencyPhonesEnabled$ = this.store.select(fromConfigSelectors.selectAreEmergencyPhonesEnabled);

  websiteGuid$ = this.store.select(fromConfigSelectors.selectWebsiteGuid);
  selectedCurrency$ = this.store.select(fromConfigSelectors.selectedCurrency);
  defaultCurrency$ = this.store.select(fromConfigSelectors.selectDefaultCurrency);
  userRequiresApproval$ = this.store.select(fromConfigSelectors.selectUserRequiresApproval);
  requireApprovalForOutOfPolicyOnly$ = this.store.select(fromConfigSelectors.selectRequireApprovalForOutOfPolicyOnly);
  assignedApprovers$ = this.store.select(fromConfigSelectors.assignedApprovers);
  hasApprovers$ = this.store.select(fromConfigSelectors.hasApprovers);
  hasMultipleApprovers$ = this.store.select(fromConfigSelectors.selectHasMultipleApprovers);
  isCurrencyInitialized$ = this.store.select(fromConfigSelectors.selectIsCurrencyInitialize);
  selectCompanyPaymentData$ = this.store.select(fromConfigSelectors.selectCompanyPaymentPageData);
  topRegions$: Observable<AutocompleteRegion[]> = this.store.pipe(
    select(fromConfigSelectors.topLocations),
    map((regions: TopRegion[]) =>
      regions.map((region) => ({
        heading: region.region,
        options: region.locations.map((location) => ({
          LocationName: location.name,
          LocationCode: location.code || '',
          LocationUrl: location.url,
        })),
      })),
    ),
  );

  userSearchConfig$ = this.store.select(fromConfigSelectors.userSearchConfiguration);

  recentlyBookedHotels$ = this.store.select(fromConfigSelectors.recentlyBookedHotels);

  bookingToMyself$ = this.store.select(fromConfigSelectors.selectBookingToMyself);

  contactQueryParams$ = this.store.select(fromConfigSelectors.selectContactQueryParams);

  analyticsEnabled$ = this.store.select(fromConfigSelectors.selectAnalyticsEnabled);
  reportsCurrency$ = this.store.select(fromConfigSelectors.selectAnalyticsCurrency);
  isCustomReportAvailable$ = this.store.select(fromConfigSelectors.selectIsCustomReportAvailable);
  availableReports$ = this.store.select(fromConfigSelectors.selectAvailableReports);

  isConfigurationLoaded$ = this.store.select(fromConfigSelectors.selectIsConfigurationLoaded);

  displayUsTaxesAndFees$ = this.store.select(fromConfigSelectors.selectDisplayUsTaxesAndFees);
  hideDueAtCheckIn$ = this.store.select(fromConfigSelectors.selectHideDueAtCheckIn);

  isPayPortalEnabled$ = this.isConfigurationLoaded$.pipe(
    skipWhile((isConfigurationLoaded) => !isConfigurationLoaded),
    withLatestFrom(
      this.store.select(fromConfigSelectors.selectIsRoomexPayPortalEnabled),
      (_, isPayPortalEnabled) => isPayPortalEnabled,
    ),
  );

  allowCompanyPaymentPage$ = this.isConfigurationLoaded$.pipe(
    skipWhile((isConfigurationLoaded) => !isConfigurationLoaded),
    withLatestFrom(
      this.store.select(fromConfigSelectors.selectAllowCompanyPaymentPage),
      (_, allowCompanyPaymentPage) => allowCompanyPaymentPage,
    ),
  );

  hasTrainlineAccount$ = this.store.select(fromConfigSelectors.selectHasTrainlineAccount);

  constructor(private store: Store<ConfigState>) {}

  loadBaseConfiguration(): void {
    this.store.dispatch(baseConfigActions.loadBaseConfig());
  }

  loadClientConfiguration(): void {
    this.store.dispatch(clientConfig.load());
  }

  initializeLocale(): void {
    this.store.dispatch(localeActions.initializeLocale());
  }

  changeLocale(locale: string): void {
    this.store.dispatch(localeActions.changeLocale({ newLocale: locale }));
  }

  generateCidSucceed(cid: string): void {
    this.store.dispatch(cidActions.generateCidSucceed({ guid: cid }));
  }

  readCidSucceed(cid: string): void {
    this.store.dispatch(cidActions.readCidSucceed({ guid: cid }));
  }

  setSelectedCurrency(code: string): void {
    this.store.dispatch(setSelectedCurrency({ payload: code }));
  }

  updateRecentlyBookedHotel(hotel: RecentlyBookedHotel): void {
    this.store.dispatch(updateRecentlyBookedHotel({ recentlyBookedHotel: hotel }));
  }

  private isFeatureEnabled(featureName: FeatureFlagEnum): Observable<boolean> {
    return this.store.select(fromConfigSelectors.selectFeatureEnabled(featureName));
  }
}
